import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useKnowledgebase } from '../../Contexts/KnowledgebaseContext';
import SearchView from './SearchView';
import ArticleView from './ArticleView';
import FAQView from './FAQView';
import VideoView from './VideoView';
import OnboardingChecklist from './OnboardingChecklist';
import ReleaseNotesView from './ReleaseNotesView';
import AIAssistantView from './AIAssistantView';

export default function KnowledgebasePanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isOpen, activeView, closePanel } = useKnowledgebase();
  const searchInputRef = useRef(null);

  // Move focus to search input when panel opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to allow the drawer animation to complete
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const renderView = () => {
    switch (activeView) {
      case 'search':
        return <SearchView searchInputRef={searchInputRef} />;
      case 'article':
        return <ArticleView />;
      case 'faq':
        return <FAQView />;
      case 'videos':
        return <VideoView />;
      case 'onboarding':
        return <OnboardingChecklist />;
      case 'release-notes':
        return <ReleaseNotesView />;
      case 'ai-assistant':
        return <AIAssistantView />;
      default:
        return <SearchView searchInputRef={searchInputRef} />;
    }
  };

  const panel = (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closePanel}
      variant="temporary"
      PaperProps={{
        sx: {
          width: isMobile ? '100vw' : 480,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      // Ensure it renders above AG Grid and other MUI modals
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      {/* Panel header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" component="h2">
          Help &amp; Documentation
        </Typography>
        <IconButton
          aria-label="Close help panel"
          onClick={closePanel}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Panel content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {renderView()}
      </Box>
    </Drawer>
  );

  // Render via portal to avoid z-index conflicts with AG Grid
  return ReactDOM.createPortal(panel, document.body);
}
